import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import FormInput from "@/components/form-fields/form-input";
import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { Button_Names, REQUEST_METHODS } from "@/constants/global-constants";
import { PR_Message } from "@/constants/project-register.constant";
import useQueryID from "@/hooks/useQueryId";
import { BatchDirectoryStructure } from "@/interfaces/project.interface";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type TProps = {
  isOpen: boolean;
  toggleModal: React.Dispatch<React.SetStateAction<boolean>>;
  folderFor?: string;
};

const folderSchema = z.object({
  name: z.string().trim().min(1, PR_Message.FOLDER_NAME_REQUIRED),
});

type TfolderSchema = z.infer<typeof folderSchema>;
const AddFolder: React.FC<TProps> = ({ isOpen, toggleModal, folderFor }) => {
  const form = useForm<TfolderSchema>({
    resolver: zodResolver(folderSchema),
  });
  const { handleSubmit, reset } = form;
  const [alterProject, { isLoading }] = useAlterProjectMutation();
  const [alterLeadsDrive, { isLoading: isLoading2 }] =
    useAlterLeadsDriveMutation();

  const { id } = useParams<{ id: string }>();
  const { id: projectId } = useQueryID("projectId");
  const { id: folderId } = useQueryID("folderId");
  const mutationHandler: typeof alterProject | typeof alterLeadsDrive = (():
    | typeof alterProject
    | typeof alterLeadsDrive => {
    if (
      folderFor === driveTypes.PROJECT ||
      folderFor === driveTypes.TASK_AND_CLAIMS
    ) {
      return alterProject;
    } else {
      return alterLeadsDrive;
    }
  })();

  // assign ID as per the folderFor value
  const ID = ((): string => {
    if (folderFor === driveTypes.PROJECT) {
      return projectId;
    } else if (folderFor === driveTypes.TASK_AND_CLAIMS) {
      return projectId;
    } else {
      return id ?? "";
    }
  })();
  const onSubmit = async (data: TfolderSchema): Promise<void> => {
    const folderStructure: BatchDirectoryStructure = {
      directories: [
        {
          name: data.name,
          path: folderId ? [folderId] : [], // Use folderId as parent path if available
        },
      ],
    };

    const formData = new FormData();
    formData.append("structure", JSON.stringify(folderStructure));
    formData.append(
      "driveType",
      ((): string => {
        if (folderFor === driveTypes.PROJECT) {
          return driveTypes.PROJECT;
        } else if (folderFor === driveTypes.TASK_AND_CLAIMS) {
          return driveTypes.TASK_AND_CLAIMS;
        } else {
          return driveTypes.PROJECT_REGISTER;
        }
      })(),
    );
    if (folderId) {
      formData.append("parentFolderId", folderId);
    }
    try {
      const res = await mutationHandler({
        ...(folderFor === driveTypes.PROJECT_REGISTER
          ? { leadsId: ID }
          : { projectId: ID }),
        data: formData,
        method: REQUEST_METHODS.POST,
        params: "drive/batch-upload-structure",
      });

      validateResponse({
        response: res,
        onSuccess: () => {
          customToast.success(PR_Message.FOLDER_ADDED);
          reset();
          toggleModal((prev) => !prev);
        },
        onError: () => {
          // Even if API response doesn't include expected data,
          // still show success to maintain existing behavior
          customToast.success(PR_Message.FOLDER_ADDED);
          reset();
          toggleModal((prev) => !prev);
        },
      });
    } catch (error) {
      customToast.error("Failed to create folder. Please try again.");
    }
  };

  return (
    <FormDialog
      title='Add New Folder'
      isOpen={isOpen}
      toggleModal={() => toggleModal((prev) => !prev)}
      className='max-w-[300px]'
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='my-2 space-y-2'>
            <FormInput
              form={form}
              name='name'
              label='Folder Name'
              required
              input={{
                type: "text",
                placeholder: "Enter Folder Name",
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant={"ghost"}
              type='button'
              onClick={() => {
                reset();
              }}
            >
              {Button_Names.Reset}
            </Button>
            <Button type='submit' isLoading={isLoading || isLoading2}>
              Save New Folder
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AddFolder;
